import React, { useContext } from 'react';
import gql from 'graphql-tag';
import * as I18n from 'i18n-js';
import { useMutation } from '../lib/use_mutation_hook';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import { MutationFn } from 'react-apollo';
import {
  CoreCheckoutSelectCreditCard,
} from '../schemas/rql';
import { CheckoutOrderSummaryFragment } from './checkout_order_summary';
import { Paths } from './shared/url_helpers';

import ModalDialog from '@reverbdotcom/commons/src/components/modal_dialog';
import LoadingOverlay from '../shared/loading_overlay';
import { CheckoutCreditCardWalletFragment } from './checkout_credit_card_wallet';
import { WithRouterProps } from 'react-router';
import { RCAlertBox } from '@reverbdotcom/commons/src/rc/RCAlertBox';

interface CreditCardForm {
  id: string; // HTML Form ID
  onSelect: (creditCardId: string) => Promise<void>;
  selectedShippingAddressId: string;
}
interface ExternalProps {
  children: React.ReactNode;
}

export const CreditCardFormContext = React.createContext<CreditCardForm>({
  id: null,
  onSelect: null,
  selectedShippingAddressId: null,
});
interface MutationProps {
  selectCheckoutCreditCard: SelectCheckoutCreditCardMutationFn;
}

type Props = MutationProps & WithRouterProps & ExternalProps;

type SelectCheckoutCreditCardMutationFn = MutationFn<CoreCheckoutSelectCreditCard.Mutation, CoreCheckoutSelectCreditCard.Variables>;

export function CreditCardWalletModal(props: Props) {
  const { query } = props.location;
  const selectedShippingAddressId = query.selected_shipping_address_id;
  const selectedCardId = query.selected_card_id;

  const isCreditCardCreateFormPage = props.router.isActive({
    pathname: Paths.checkoutCreateCreditCard.expand({
      checkoutId: props.params.id,
    }),
  });

  function buildCreditCardForm(): CreditCardForm {
    const id = isCreditCardCreateFormPage
      ? 'credit-card-create'
      : 'credit-card-select';

    return {
      id,
      onSelect,
      selectedShippingAddressId,
    };
  }

  const form = buildCreditCardForm();

  const { selectCheckoutCreditCard, loading, errors } = useSelectCheckoutCreditCard(
    props.params.id,
    props.selectCheckoutCreditCard,
  );

  function onRequestClose() {
    const checkoutPath = Paths.checkoutSinglePageIndex.expand({ checkoutId: props.params.id });
    props.router.push(checkoutPath);
  }

  function onBack() {
    if (isCreditCardCreateFormPage) {
      const cardIndexPath = Paths.checkoutCreditCardWallet.expand({
        checkoutId: props.params.id,
        selected_card_id: selectedCardId,
        selected_shipping_address_id: selectedShippingAddressId,
      });
      props.router.push(cardIndexPath);
    } else {
      onRequestClose();
    }
  }

  function headerTitle() {
    if (isCreditCardCreateFormPage) {
      return I18n.t('discovery.creditCardWallet.modal.addNew.title');
    } else {
      return I18n.t('discovery.creditCardWallet.modal.index.title');
    }
  }

  function saveButtonText() {
    if (isCreditCardCreateFormPage) {
      return I18n.t('discovery.creditCardWallet.modal.addNew.saveButton');
    } else {
      return I18n.t('discovery.creditCardWallet.modal.index.saveButton');
    }
  }

  function backButtonText() {
    if (isCreditCardCreateFormPage) {
      return I18n.t('discovery.creditCardWallet.modal.addNew.backButton');
    } else {
      return I18n.t('discovery.creditCardWallet.modal.index.cancel');
    }
  }

  async function onSelect(creditCardId: string) {
    const result = await selectCheckoutCreditCard({ creditCardId });

    if (result) {
      onRequestClose();
    }
  }

  return (
    <ModalDialog
      headerTitle={headerTitle()}
      saveButtonText={saveButtonText()}
      backButtonText={backButtonText()}
      onRequestClose={onRequestClose}
      onBack={onBack}
      formId={form.id}
      containsFormElement
      isOpen
    >
      <LoadingOverlay showOverlay={loading}>
        {errors.length > 0 && (
          <div className="mb-1">
            <RCAlertBox type="error">
              {errors.map(e => <li key={`${e.field}-${e.message}`}>{e.message}</li>)}
            </RCAlertBox>
          </div>
        )}
        <CreditCardFormContext.Provider value={form}>
          {props.children}
        </CreditCardFormContext.Provider>
      </LoadingOverlay>
    </ModalDialog>
  );
}

export function useSelectCheckoutCreditCard(
  checkoutId: string,
  selectCheckoutCreditCard: SelectCheckoutCreditCardMutationFn,
) {
  const [mutate, { loading, errors }] = useMutation(selectCheckoutCreditCard);

  const selectCheckoutCreditCardCallback = React.useCallback(async ({ creditCardId }) => {
    return mutate({
      variables: {
        input: {
          id: checkoutId,
          creditCardId,
        },
      },
    });
  }, [checkoutId, mutate]);

  return { selectCheckoutCreditCard: selectCheckoutCreditCardCallback, loading, errors };
}

const withMutation = withGraphql<Props, CoreCheckoutSelectCreditCard.Mutation, CoreCheckoutSelectCreditCard.Variables>(
  gql`
    mutation Core_Checkout_SelectCreditCard(
      $input: Input_core_apimessages_SelectCheckoutCreditCardRequest
    ) {
      selectCheckoutCreditCard(input: $input) {
        checkout {
          _id
          ...CheckoutOrderSummaryFields
          ...CheckoutCreditCardWalletFields
        }
      }
    }
    ${CheckoutOrderSummaryFragment}
    ${CheckoutCreditCardWalletFragment}
  `,
  {
    name: 'selectCheckoutCreditCard',
  },
);

export function useCreditCardForm() {
  return useContext(CreditCardFormContext);
}

export default withMutation(CreditCardWalletModal);
